import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar, Toolbar, IconButton, Box, Button, Popover, useTheme, useMediaQuery, Drawer, List, ListItem, ListItemText, Collapse, styled
} from '@mui/material';
import { Menu as MenuIcon, Add, Remove } from '@mui/icons-material';
import { menuItems } from './MenuItems';

const CustomIconButton = styled(IconButton)({
  color: '#f39c12',
  marginLeft: -8,
  verticalAlign: 'middle',
  padding: 0,
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

const renderStyledButton = (menuItem, theme, isDrawer = false) => (
  <Button
    key={menuItem.title}
    component={RouterLink}
    to={menuItem.path}
    sx={{
      bgcolor: 'primary.main',
      color: 'white',
      textTransform: 'none',
      borderRadius: isDrawer ? '5rem' : '1.25rem',
      padding: isDrawer ? '1rem' : 'none',
      marginRight: isDrawer ? 0 : -2,
      '&:hover': {
        bgcolor: '#f39c12',
        color: 'white',
      },
      fontWeight: 'bold',
      fontSize: isDrawer ? '0.875rem' : '1rem',
      maxWidth: isDrawer ? '100%' : 'none',
      width: isDrawer ? '30%' : 'auto',
      margin: isDrawer ? theme.spacing(1) : 'none',
    }}
  >
    {menuItem.title}
  </Button>
);

const NavBar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const menuItemRefs = useRef(new Map());

  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [isMobile]);

  const handleMouseEnter = useCallback((title) => {
    setOpenSubMenu(prevState => ({ ...prevState, [title]: true }));
  }, []);

  const handleMouseLeave = useCallback((title) => {
    setOpenSubMenu(prevState => ({ ...prevState, [title]: false }));
  }, []);

  const assignRefsForMenuItem = useCallback((title) => {
    if (!menuItemRefs.current.has(title)) {
      menuItemRefs.current.set(title, {
        menuItemRef: React.createRef(),
        submenuRef: React.createRef(),
      });
    }
    return menuItemRefs.current.get(title);
  }, []);

  useEffect(() => {
    const checkIfSubMenuShouldStayOpen = (event) => {
      menuItemRefs.current.forEach((value, key) => {
        const menuItemNode = value.menuItemRef.current;
        const submenuNode = value.submenuRef.current;
        if (menuItemNode && submenuNode) {
          const menuItemRect = menuItemNode.getBoundingClientRect();
          const submenuRect = submenuNode.getBoundingClientRect();
          const isInMenuItemOrSubmenu = (
            event.clientX >= menuItemRect.left && event.clientX <= menuItemRect.right &&
            event.clientY >= menuItemRect.top && event.clientY <= menuItemRect.bottom
          ) || (
              event.clientX >= submenuRect.left && event.clientX <= submenuRect.right &&
              event.clientY >= submenuRect.top && event.clientY <= submenuRect.bottom
            );
          if (!isInMenuItemOrSubmenu) {
            handleMouseLeave(key);
          }
        }
      });
    };

    document.addEventListener('mousemove', checkIfSubMenuShouldStayOpen);
    return () => document.removeEventListener('mousemove', checkIfSubMenuShouldStayOpen);
  }, [handleMouseLeave]);

  const renderNavIcon = (title) => (
    <CustomIconButton size="small">
      {openSubMenu[title] ? <Remove fontSize="small" /> : <Add fontSize="small" />}
    </CustomIconButton>
  );

  const toggleMobileDrawer = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSubMenuToggle = (title, event) => {
    event.stopPropagation();
    setOpenSubMenu(prevState => ({
      ...prevState,
      [title]: !prevState[title],
    }));
  };

  const drawerWidth = 360;
  const drawer = (
    <Box sx={{ background: '#000', color: 'white', textAlign: 'center', width: drawerWidth, mt: theme.spacing(9), overflowX: 'hidden' }}>
      <List>
        {menuItems.map((item) => (
          <React.Fragment key={item.title}>
            {item.button ? (
              <ListItem sx={{ justifyContent: 'flex-start' }}>
                {renderStyledButton(item, theme, true)}
              </ListItem>
            ) : (
              <ListItem
                button
                component={item.subMenu && item.subMenu.length ? 'div' : RouterLink}
                to={!item.subMenu || !item.subMenu.length ? item.path : undefined}
                onClick={(event) => item.subMenu && item.subMenu.length && handleSubMenuToggle(item.title, event)}
                sx={{
                  justifyContent: 'flex-start',
                  '& .MuiListItemText-primary': {
                    fontWeight: 'bold',
                    color: 'white',
                    '&:hover': {
                      color: '#f39c12',
                    },
                  },

                }}
              >
                <ListItemText primary={item.title} />
                {item.subMenu && item.subMenu.length ? <CustomIconButton size="small" onClick={(event) => handleSubMenuToggle(item.title, event)}>{openSubMenu[item.title] ? <Remove fontSize="small" /> : <Add fontSize="small" />}</CustomIconButton> : null}
              </ListItem>
            )}
            {item.subMenu && item.subMenu.length > 0 && (
              // Submenu items
              <Collapse in={Boolean(openSubMenu[item.title])} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subMenu.map((subItem) => (
                    <ListItem
                      button
                      key={subItem.title}
                      component={RouterLink}
                      to={subItem.path}
                      onClick={toggleMobileDrawer}
                      sx={{
                        pl: theme.spacing(4),
                        pr: theme.spacing(1),
                        mr: theme.spacing(1),
                        fontSize: '0.875rem',
                        color: 'rgba(255, 255, 255, 0.7)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
                        '&:hover': {
                          color: '#f39c12',
                          transform: 'translateX(20px)',
                          transition: 'transform 0.5s ease-in-out',
                        },
                        '&:not(:last-child)': {
                          mb: '0.25rem',
                        },
                      }}
                    >
                      <ListItemText primary={subItem.title} />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <AppBar position="fixed" sx={{ width: '100%', background: '#000', boxShadow: 'none', zIndex: theme.zIndex.drawer + 1, overflowX: 'hidden', padding: theme => `${theme.spacing(0, 1)} !important` }}>
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: '64px' }}>
        <RouterLink to="/" sx={{ display: 'flex', alignItems: 'center' }}>
          <img src='assets/img/logo.png' alt='Logo' style={{ height: 'auto', maxWidth: '100px' }} />
        </RouterLink>
        {isMobile ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexGrow: 1, mr: 0 }}>
            {menuItems.map((menuItem) => {
              const hasSubMenu = menuItem.subMenu && menuItem.subMenu.length > 0;
              const { menuItemRef, submenuRef } = assignRefsForMenuItem(menuItem.title);

              if (menuItem.button) {
                return renderStyledButton(menuItem, theme);
              }
              return (
                <React.Fragment key={menuItem.title}>
                  <Box
                    ref={menuItemRef}
                    onMouseEnter={() => handleMouseEnter(menuItem.title)}
                    onMouseLeave={() => handleMouseLeave(menuItem.title)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                      '&:hover': {
                        color: '#f39c12',
                      },
                    }}
                  >
                    <Button
                      aria-controls={hasSubMenu ? 'submenu-list' : undefined}
                      aria-expanded={hasSubMenu ? openSubMenu[menuItem.title] : undefined}
                      sx={{
                        color: 'white',
                        display: 'block',
                        padding: theme.spacing(0.8),
                        '&:hover': {
                          bgcolor: 'transparent',
                          color: '#f39c12',
                        },
                        fontWeight: 'bold',
                      }}
                      component={hasSubMenu ? undefined : RouterLink}
                      to={hasSubMenu ? undefined : menuItem.path}
                    >
                      {menuItem.title}
                    </Button>
                    {hasSubMenu && renderNavIcon(menuItem.title)}
                    {hasSubMenu && (
                      <Popover
                        id={menuItem.title + '-submenu'}
                        open={Boolean(openSubMenu[menuItem.title])}
                        anchorEl={menuItemRef.current}
                        onClose={() => handleMouseLeave(menuItem.title)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        sx={{
                          '.MuiPaper-root': {
                            bgcolor: '#000',
                            color: '#fff',
                            // maxWidth: 360,
                            // maxWidth: theme.breakpoints.values.lg,
                            // position: 'absolute',
                          },
                        }}
                      >
                        <Box ref={submenuRef}>
                          <List component="nav" aria-labelledby="nested-list-submenu">
                            {menuItem.subMenu.map((subItem) => (
                              <ListItem
                                key={subItem.title}
                                button
                                component={subItem.isExternal ? 'a' : RouterLink}
                                to={subItem.isExternal ? undefined : subItem.path}
                                href={subItem.isExternal ? subItem.path : undefined}
                                target={subItem.isExternal ? '_blank' : undefined}
                                rel={subItem.isExternal ? 'noopener noreferrer' : undefined}
                                sx={{
                                  color: '#fff',
                                  '&:hover': {
                                    bgcolor: 'transparent',
                                    color: '#f39c12',
                                    transform: 'translateX(10px)',
                                    transition: 'transform 0.5s ease-in-out',
                                  },
                                }}
                              >
                                <ListItemText primary={subItem.title} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </Popover>
                    )}
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
        ) : (
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="menu"
            sx={{ ml: 'auto' }}
            onClick={toggleMobileDrawer}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={toggleMobileDrawer}
          sx={{
            '.MuiDrawer-paper': {
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Toolbar>
    </AppBar >
  );
};

export default NavBar;




// import React, { useState } from "react";
// import { Link } from "react-router-dom";

// const NavBar = () => {
//   const [active, setActive] = useState(false);
//   const menuActive = () => {
//     setActive(!active);
//   };

//   // Control sidebar navigation
//   let items = document.querySelectorAll(".menu-item-has-children > a");
//   for (let i in items) {
//     if (items.hasOwnProperty(i)) {
//       items[i].onclick = function () {
//         this.parentElement
//           .querySelector(".sub-menu")
//           .classList.toggle("active");
//         this.classList.toggle("open");
//       };
//     }
//   }

//   return (
//     <>
//       {/* navbar start */}
//       <nav className='navbar navbar-area navbar-expand-lg' id='nav-container'>
//         <div className='nav-container navbar-bg'>
//           <div className='responsive-mobile-menu'>
//             <button
//               onClick={menuActive}
//               className={
//                 active
//                   ? "menu toggle-btn d-block d-lg-none open"
//                   : "menu toggle-btn d-block d-lg-none"
//               }
//               data-target='#itech_main_menu'
//               aria-expanded='false'
//               aria-label='Toggle navigation'
//             >
//               <span className='icon-left' />
//               <span className='icon-right' />
//             </button>
//           </div>

//           <div className={active ? "collapse navbar-collapse sopen" : "collapse navbar-collapse"} id='itech_main_menu' >
//             <div className='logo'>
//               <Link to='/'>
//                 <img src='assets/img/logo.png' alt='Logo' />
//               </Link>
//             </div>

//             <ul className='navbar-nav menu-open text-lg-end'>
//               <li className='menu-item-has-children'>
//                 <a href='#'>WHO WE ARE</a>
//                 <ul className='sub-menu'>
//                   <li>
//                     <Link to='/overview'>OVERVIEW</Link>
//                   </li>
//                   <li>
//                     <Link to='/board-of-directors'>BOARD OF DIRECTORS</Link>
//                   </li>
//                   <li>
//                     <Link to='/friends-of-beads'>FRIENDS OF BEADS</Link>
//                   </li>
//                   <li>
//                     <Link to='/press'>PRESS</Link>
//                   </li>
//                   <li>
//                     <Link to='/mentorship-program'>BEADS MENTORSHIP PROGRAM</Link>
//                   </li>
//                   <li>
//                     <Link to='/beading-groups'>WOMEN'S BEADING GROUPS</Link>
//                   </li>
//                   <li>
//                     <Link to='/changemaker-projects'>CHANGEMAKERS PROJECT</Link>
//                   </li>
//                 </ul>
//               </li>
//               <li className='menu-item-has-children'>
//                 <a href='#'>WHAT WE DO</a>
//                 <ul className='sub-menu'>
//                   <li>
//                     <Link to='/overview-tab'>OVERVIEW</Link>
//                   </li>
//                   <li>
//                     <Link to='/interns-program'>BEADS INTERN PROGRAM</Link>
//                   </li>
//                   <li>
//                     <Link to='/coming-of-age'>COMING OF AGE</Link>
//                   </li>
//                   <li>
//                     <Link to='/teachers-training'>TEACHERS TRAINING</Link>
//                   </li>
//                   <li>
//                     <Link to='/library-development'>LIBRARY DEVELOPMENTS</Link>
//                   </li>
//                   <li>
//                     <Link to='/school-sponsorships'>SCHOOL SPONSORSHIP</Link>
//                   </li>
//                   <li>
//                     <a href='https://climatechange-makers.netlify.app/' target='_blank' rel='noopener noreferrer'>CLIMATE CHANGE MAKERS IN SUB-SAHARAN AFRICA</a>
//                   </li>
//                   <li>
//                     <Link to='/famine-relief'>FAMINE RELIEF</Link>
//                   </li>
//                   <li>
//                     <Link to='/carol-success-story'>CAROL SUCCESS STORY AND COVID HELP</Link>
//                   </li>
//                 </ul>
//               </li>
//               <li>
//                 <Link to='/success-stories'>SUCCESS STORIES</Link>
//               </li>
//               <li className='menu-item-has-children'>
//                 <a href='#'>BEADS HIGH SCHOOL</a>
//                 <ul className='sub-menu'>
//                   <li>
//                     <Link to='/tembea-overview'>OVERVIEW</Link>
//                   </li>
//                   <li>
//                     <Link to='/funding-needs'>FUNDING NEEDS</Link>
//                   </li>
//                 </ul>
//               </li>
//               <li className='menu-item-has-children'>
//                 <a href='#'>GET INVOLVED</a>
//                 <ul className='sub-menu'>
//                   <li>
//                     <Link to='/school-sponsorships'>OVERVIEW</Link>
//                   </li>
//                   <li>
//                     <Link to='/donations-and-alternative-gifts'>DONATIONS AND ALTERNATIVE GIFTS</Link>
//                   </li>
//                   <li>
//                     <Link to='/sponsors-trips'>SPONSORS TRIPS</Link>
//                   </li>
//                 </ul>
//               </li>
//               <li>
//                 <Link to='/news'>NEWS</Link>
//               </li>
//               <li>
//                 <Link to='/contact'>CONTACT US</Link>
//               </li>
//               <li>
//                 <Link to="/donate" className="btn-xs donate"> DONATE</Link>
//               </li>
//             </ul>
//           </div>
//         </div>
//       </nav>
//     </>
//   );
// };

// export default NavBar;
